"use client";
import React, { useState, useEffect } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { getYearOnYear } from "../../api/transactioApi/transactionsApi";
import { YearOnYear } from "../../types/transactions";
import "./YearOnYearChart.scss";

const CURRENT_YEAR = new Date().getFullYear();

function useWindowWidth() {
  const [width, setWidth] = React.useState(window.innerWidth);
  React.useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return width;
}

const YearOnYearChart: React.FC = () => {
  const [selectedYear, setSelectedYear] = useState<number>(CURRENT_YEAR);
  const [data, setData] = useState<YearOnYear | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    async function load() {
      try {
        setLoading(true);
        setError(null);
        const res = await getYearOnYear(selectedYear);
        if (mounted) setData(res);
      } catch (err: any) {
        if (mounted) setError(err?.message || "Failed to load year-on-year data.");
      } finally {
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => { mounted = false; };
  }, [selectedYear]);

  const yearOptions = Array.from({ length: 5 }, (_, i) => CURRENT_YEAR - i);

  const windowWidth = useWindowWidth();
  const barSize = windowWidth < 480 ? 14 : windowWidth < 768 ? 20 : 30;
  // Keep at least 50px per month pair so bars never squish
  const chartMinWidth = Math.max(windowWidth < 768 ? 500 : 0, data ? data.months.length * 55 : 0);
  const growthSign = data && data.growth_pct >= 0 ? "+" : "";

  return (
    <div className="yearonyear-container">
      <div className="yoy-header">
        <span className="yoy-title">Year on Year</span>
        <select
          className="yoy-year-select"
          value={selectedYear}
          onChange={(e) => setSelectedYear(Number(e.target.value))}
        >
          {yearOptions.map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="loader-container small"><div className="loader" /></div>
      ) : error ? (
        <div className="error-box"><p>⚠️ {error}</p></div>
      ) : data ? (
        <>
          <div className="yoy-summary">
            <div className="yoy-card">
              <h4>{growthSign}{data.growth_pct}%</h4>
              <p>Growth vs last year</p>
            </div>
            <div className="yoy-card">
              <h4>{data.current_year_count.toLocaleString()}</h4>
              <p>Current year transactions</p>
            </div>
            <div className="yoy-card">
              <h4>{data.previous_year_count.toLocaleString()}</h4>
              <p>Previous year transactions</p>
            </div>
          </div>

          <div className="yoy-barchart">
            <div className="yoy-chart-scroll">
              <div style={{ minWidth: chartMinWidth || "100%" }}>
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart data={data.months} margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="month" tick={{ fontSize: windowWidth < 480 ? 10 : 12 }} />
                    <YAxis tick={{ fontSize: windowWidth < 480 ? 10 : 12 }} width={windowWidth < 480 ? 28 : 40} />
                    <Tooltip />
                    <Legend verticalAlign="top" align="right" />
                    <Bar dataKey="current" fill="#111827" barSize={barSize} name="Current Year" />
                    <Bar dataKey="previous" fill="#9ca3af" barSize={barSize} name="Previous Year" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
};

export default YearOnYearChart;
